import { ExplainMyResultsProps } from 'components/page-header/explain-my-results-modal/explain-my-results-modal.type';
import { useTranslation } from 'hooks';
import { FC, useEffect, useState } from 'react';
import { ICONS } from 'shared/constants/images';
import Button from 'shared/ui/button/button';
import { Icon } from 'shared/ui/icon/icon';
import { Modal } from 'shared/ui/modal/modal';
import { ModalActions } from 'shared/ui/modal/modal-actions/modal-actions';
import { Loader } from 'shared/ui/loader/loader';
import {
  ACE_SUBDOMAIN,
  ACE_WSUUID,
  getAceMeetingLink,
  loadAceWidget,
} from 'services/ace/aceLoader';
import cls from './explain-my-results-modal.module.scss';

export const ExplainMyResultsModal: FC<ExplainMyResultsProps> = ({
  onClose,
  isOpen,
}) => {
  const { t, currentLocale } = useTranslation();
  const aceMeetingLink = getAceMeetingLink(currentLocale);
  const [isBooking, setIsBooking] = useState(false);
  const [aceStatus, setAceStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );

  useEffect(() => {
    if (!isBooking) {
      return;
    }
    let isActive = true;
    loadAceWidget()
      .then(() => isActive && setAceStatus('ready'))
      .catch(() => isActive && setAceStatus('error'));
    return () => {
      isActive = false;
    };
  }, [isBooking]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleACE = () => {
    setIsBooking(true);
  };

  if (isBooking) {
    return (
      <Modal
        onClose={onClose}
        isOpen={isOpen}
        className={`${cls.modal} ${cls.modalBooking}`}
      >
        <div className={cls.widget}>
          {aceStatus === 'loading' && <Loader size={32} color='currentColor' />}
          {(aceStatus === 'error' ||
            (aceStatus === 'ready' &&
              !(aceMeetingLink && ACE_SUBDOMAIN && ACE_WSUUID))) && (
            <p>{t('aceLoadError')}</p>
          )}
          {aceStatus === 'ready' &&
            aceMeetingLink &&
            ACE_SUBDOMAIN &&
            ACE_WSUUID && (
              <ace-calendar
                data-testid='ace-calendar'
                meeting-link={aceMeetingLink}
                subdomain={ACE_SUBDOMAIN}
                wsuuid={ACE_WSUUID}
              >
                {/* empty slot containers required by the ACE snippet */}
                <div id='pyes' slot='pyes-c'></div>
                <div id='pyepp' slot='pyepp-c'></div>
              </ace-calendar>
            )}
        </div>
        <ModalActions>
          <Button onClick={onClose} theme='text' tone='default' size='m'>
            {t('cancel')}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  const explainMyResultsData = [
    {
      icon: ICONS.resultsChart,
      heading: t('understandYourResults'),
      description: t('seeHowAI'),
    },
    {
      icon: ICONS.list,
      heading: t('knowWhatToImprove'),
      description: t('identifyTheChanges'),
    },
    {
      icon: ICONS.answerMessage,
      heading: t('getAnswers'),
      description: t('discussYourBusiness'),
    },
  ];

  return (
    <Modal onClose={onClose} isOpen={isOpen} className={cls.modal}>
      <div className={cls.title}>
        <h2>{t('turnYourVisibility')}</h2>
        <p>{t('bookAFree30MinCall')}</p>
      </div>
      <div className={cls.details}>
        {explainMyResultsData.map(e => {
          return (
            <div key={e.heading} className={cls.detailsContainer}>
              <div className={cls.detailsSvg}>
                <Icon pathToIcon={e.icon} />
              </div>
              <div className={cls.detailsTitle}>
                <h3>{e.heading}</h3>
                <p>{e.description}</p>
              </div>
            </div>
          );
        })}
      </div>
      <ModalActions>
        <Button onClick={onClose} theme='text' tone='default' size='m'>
          {t('notNow')}
        </Button>
        <Button onClick={handleACE} theme='filled' tone='default' size='m'>
          {t('bookMyFreeCall')}
        </Button>
      </ModalActions>
    </Modal>
  );
};
